import Markdown from "react-markdown";
import type { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";

// Markdown-Renderer fuer Assistant-Antworten (SIMO-CHAT-MARKDOWN-RENDERING-1).
// Sicherheit: react-markdown rendert per Default kein rohes HTML (kein rehype-raw),
// rehype-sanitize ist zusaetzliche Defense-in-Depth gegen z.B. javascript:-Links.
// Styling ueber explizite component-Overrides mit den jarvis-Theme-Klassen,
// damit keine Abhaengigkeit von @tailwindcss/typography noetig ist.
const components: Components = {
  a: ({ href, children, ...props }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-jarvis-accent underline underline-offset-2 hover:opacity-80"
      {...props}
    >
      {children}
    </a>
  ),
  p: ({ children }) => <p className="my-1.5 leading-relaxed first:mt-0 last:mb-0">{children}</p>,
  ul: ({ children }) => <ul className="my-1.5 ml-4 list-disc space-y-0.5">{children}</ul>,
  ol: ({ children }) => <ol className="my-1.5 ml-4 list-decimal space-y-0.5">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  h1: ({ children }) => <h1 className="mb-1.5 mt-3 text-base font-semibold first:mt-0">{children}</h1>,
  h2: ({ children }) => <h2 className="mb-1.5 mt-3 text-sm font-semibold first:mt-0">{children}</h2>,
  h3: ({ children }) => <h3 className="mb-1 mt-2 text-sm font-semibold first:mt-0">{children}</h3>,
  code: ({ className, children, ...props }) => {
    const isBlock = /language-/.test(className ?? "");
    if (isBlock) {
      return (
        <code className={`${className ?? ""} block`} {...props}>
          {children}
        </code>
      );
    }
    return (
      <code className="rounded bg-black/30 px-1 py-0.5 font-mono text-[0.85em]" {...props}>
        {children}
      </code>
    );
  },
  pre: ({ children }) => (
    <pre className="my-2 overflow-x-auto rounded-lg border border-jarvis-border/60 bg-black/40 p-3 font-mono text-xs">
      {children}
    </pre>
  ),
  blockquote: ({ children }) => (
    <blockquote className="my-2 border-l-2 border-jarvis-accent/50 pl-3 italic text-jarvis-muted">
      {children}
    </blockquote>
  ),
  table: ({ children }) => (
    <div className="my-2 overflow-x-auto">
      <table className="w-full border-collapse text-xs">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border border-jarvis-border px-2 py-1 text-left font-semibold">{children}</th>
  ),
  td: ({ children }) => <td className="border border-jarvis-border px-2 py-1">{children}</td>,
  hr: () => <hr className="my-3 border-jarvis-border/50" />,
};

export function MessageMarkdown({ content }: { content: string }) {
  return (
    <div className="text-sm break-words [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
      <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]} components={components}>
        {content}
      </Markdown>
    </div>
  );
}
